import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useGetChartingCompletionReports } from "@src/appV2/Charting/api/useGetChartingCompletionReport";
import {
  CHARTING_COMPLETION_REPORT_CACHE_SETTINGS,
  MAX_PAGE_SIZE,
  MIN_START_DATE,
  VALID_CHARTING_QUALIFICATIONS,
} from "@src/appV2/Charting/constants";
import { useFetchWorkplaceWorkerTypes } from "@src/appV2/FacilityDrawer/api/useFetchWorkplaceWorkerTypes";
import { endOfDay, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";

import { type ValidChartingQualifications } from "./schema";

export function useGetInProgressRecallsCount() {
  const {
    profile: { tmz: workplaceTimezone, userId: workplaceId },
    type: userType,
  } = useSession();
  const endOfToday = endOfDay(
    utcToZonedTime(new Date(), workplaceTimezone ?? "America/Los_Angeles")
  );
  const startDate = parseISO(MIN_START_DATE);
  const { data: workerTypes } = useFetchWorkplaceWorkerTypes(
    {
      workplaceId: workplaceId ?? "",
      showHidden: false,
    },
    { enabled: isDefined(workplaceId) && userType === "FACILITY" }
  );

  const qualifications: ValidChartingQualifications[] =
    workerTypes?.data.workerTypes
      .map((workerType) => workerType.name)
      .filter((workerType): workerType is ValidChartingQualifications =>
        VALID_CHARTING_QUALIFICATIONS.includes(workerType as ValidChartingQualifications)
      ) ?? [];

  const { data: response, isFetching } = useGetChartingCompletionReports(
    {
      workplaceId: workplaceId ?? "",
      qualifications,
      startDate: startDate.toISOString(),
      endDate: endOfToday.toISOString(),
      chartingNoncomplianceState: ["review"],
      page: { size: MAX_PAGE_SIZE },
    },
    {
      enabled: isDefined(workplaceId) && userType === "FACILITY",
      ...CHARTING_COMPLETION_REPORT_CACHE_SETTINGS,
    }
  );

  const count = response?.data.length ?? 0;
  const hasNextPage = isDefined(response?.links?.next);

  return {
    count,
    inProgressRecallsCount: count,
    isFetching,
    hasNextPage,
  };
}
